<template>
  <TransitionRoot as="template" :show="props.open">
    <Dialog as="div" class="relative z-30" @close="close">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div
          class="flex min-h-full items-end justify-center items-center p-4 text-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-hidden rounded-md bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 w-full sm:max-w-lg sm:p-6"
            >
              <div class="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                <button
                  type="button"
                  class="rounded-sm bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                  @click="close"
                >
                  <span class="sr-only">Close</span>
                  <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div class="mt-3 sm:mt-0 text-left">
                <DialogTitle
                  as="h3"
                  class="text-lg font-medium leading-6 text-gray-900"
                  >{{ t("components.change_description") }}
                </DialogTitle>

                <Errors class="mt-2" />

                <div class="mt-4 flex flex-col">
                  <span class="text-sm font-medium text-gray-500">{{
                    t("validation.attributes.title")
                  }}</span>
                  <input
                    v-model="description"
                    type="text"
                    name="description"
                    id="description"
                    class="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-brand-900 focus:ring-brand-900 sm:text-sm"
                  />
                </div>
                <div class="mt-4 flex flex-col">
                  <span class="text-sm font-medium text-gray-500">{{
                    t("validation.attributes.description")
                  }}</span>
                  <textarea
                    v-model="remarks"
                    name="remarks"
                    id="remarks"
                    class="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-brand-900 focus:ring-brand-900 sm:text-sm"
                  />
                </div>
              </div>
              <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <primary-button
                  :label="t('components.buttons.save')"
                  @click="save"
                  class="sm:ml-3 w-full sm:w-auto"
                  :loading="processing"
                  :loading-label="t('components.buttons.saving')"
                />
                <secondary-button
                  :label="t('components.buttons.cancel')"
                  @click="close"
                  class="mt-3 sm:mt-0"
                />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch } from "vue";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton";
import SecondaryButton from "@/Tailwind/Components/Buttons/SecondaryButton";
import Errors from "@/Tailwind/Components/Errors.vue";

import { useI18n } from "vue-i18n";
const { t } = useI18n();

const emit = defineEmits(["save", "close"]);
const props = defineProps({
  open: Boolean,
  processing: Boolean,
  description: String,
  remarks: String,
});

watch(
  () => props.open,
  () => {
    description.value = props.description;
    remarks.value = props.remarks;
  }
);

const description = ref(props.description);
const remarks = ref(props.remarks);

const save = () => {
  emit("save", description.value, remarks.value);
};

const close = () => emit("close");
</script>
